import { Button, Dialog, Icon, setNotification, SplitColumnsContainer, TextButton, Tooltip } from '@startlibs/components'
import { Errors, TextInput, ToggleCheckbox, WithForm } from '@startlibs/form'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildValidation, required } from '../../utils/validation'
import { getJwt } from '../../hooks/useJwt'
import { jwtPostFetcher } from '../../utils/authFetch'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { getColor } from '@startlibs/utils'
import { useToggle } from '@startlibs/core'
import { DirectMessageRecordsSelectorDialog } from './DirectMessageRecordsSelectorDialog'
import { CASE_CLOSED } from '../../enums/CaseState'
// import { lazyProviderInfo } from '../../components/WithProvider'
import { lazyProviderInfoSec } from '../../components/WithProvider'
import { darken } from 'polished'
import { convertBytesAutoUnit, isUnderMBLimit } from '../../utils/utils'

const AttachamentsBox = styled.div`
  background: ${getColor('gray240')};
  height: 42px;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 0.7rem 1rem;
  font-weight: 600;
  cursor: pointer;
  :hover {
    background: ${props => darken(0.035, getColor('gray240')(props))};
  }
  :active {
    background: ${props => darken(0.06, getColor('gray240')(props))};
  }
  ${TextButton} {
    font-weight: 600;
    pointer-events: none;
  }
`

const AttachamentsIcon = styled(Icon)`
  color: ${getColor('main')};
  margin-right: 0.5rem;
  font-size: 22px;
  :hover {
    filter: brightness(0.75);
    cursor: pointer;
  }
`


const MailAdressesCard = styled.div`
  border: 1px solid #d2d2d2;
  border-radius: 6px;
  padding: 0.7rem;
  margin-bottom: 1rem;

  p {
    margin-bottom: 0.25rem;
    font-size: 13px;
  }
`

const Divider = styled.hr`
  margin-top: 1rem;
  margin-bottom: 1.5rem;    
`

const SectionLabel = styled.label`
  display: inline-block;
  font-size: 14px;
  margin-bottom: 0.5rem;
  font-weight: 600;
`

const ToggleLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
`

const SizeSummary = styled.div`
  text-align: right;
  margin-top: 0.5rem;
  font-size: 13px;
`

const SizeError = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 13px;
  line-height: 130%;
  background: rgb(195, 40, 45);
  color: white;
`

const DIRECT_MESSAGE_SIZE_LIMIT_MB = 3.5

const preValidation = buildValidation({
  "subject": [(v,_k,_values) =>  !v.length && required()],
  "message": [(v,_k,_values) => !v.length && required()],
})



export const ComposeDirectMessageDialog = ({ closeDialog, caseRequest, setCaseRequest, onSuccess = () => {} }) => {
  const intl = useIntl();
  
  const patientInfo = caseRequest.patientInfo || {};
  const firstName = String(caseRequest.firstName || patientInfo.firstName || "");
  const middleName = String((caseRequest.middleName ? caseRequest.middleName + ' ' : '') || (patientInfo.middleName ? patientInfo.middleName + ' ' : ''));
  const lastName = String(caseRequest.lastName || patientInfo.lastName || "");

  const subject = `Expert View Case ID: ${caseRequest.requestCode} - Patient ${intl.formatMessage({
    defaultMessage: "{firstName} {middleName} {lastName}",
    description: "Case request card patient name",
  }, {
    firstName,
    middleName,
    lastName
  }).replace('  ', ' ')}`;

  const message = `This message includes information and attached files pertaining to Case ID ${caseRequest.requestCode}, created in Expert View for the patient ${intl.formatMessage({
    defaultMessage: "{firstName} {middleName} {lastName}",
    description: "Case request card patient name",
  }, {
    firstName,
    middleName,
    lastName
  }).replace('  ', ' ')}. \n--- \nExpert View Direct Message  `;

  const [selectedDirectMessageRecords, setSelectedDirectMessageRecords] = useState([]);
  const fileDialog = useToggle();
  const providerInfoSec = lazyProviderInfoSec.read()
  const formApiRef = useRef(null)
  const [pdfSizeBytes, setPdfSizeBytes] = useState(null)
  const [pdfSizeError, setPdfSizeError] = useState('')
  const [includeReport, setIncludeReport] = useState(false)
  const [recordSizeMap, setRecordSizeMap] = useState({})

  useEffect(() => {
    if (caseRequest.medicalRecords && caseRequest.medicalRecords.length) {
      setRecordSizeMap(prev => {
        const next = { ...prev };
        caseRequest.medicalRecords.forEach(record => {
          if (record && record.id !== undefined) {
            const key = String(record.id);
            if (record.fileSize !== undefined) {
              next[key] = record.fileSize || 0;
            }
          }
        });
        return next;
      });
    }
  }, [caseRequest.medicalRecords]);

  const attachmentsBytes = useMemo(() => {
    return selectedDirectMessageRecords.reduce((total, recordId) => {
      const key = String(recordId);
      return total + (recordSizeMap[key] || 0);
    }, 0);
  }, [recordSizeMap, selectedDirectMessageRecords]);

  const handleFormValuesChange = useCallback((prevValues, nextValues) => {
    const prevInclude = !!prevValues?.includeReport;
    const nextInclude = !!nextValues?.includeReport;

    if (prevInclude === nextInclude) {
      return;
    }

    setIncludeReport(nextInclude);

    if (!nextInclude) {
      setPdfSizeError('');
      return;
    }

    if (caseRequest.state !== CASE_CLOSED) {
      return;
    }

    if (pdfSizeBytes !== null ) {
      return;
    }

    setPdfSizeError('');
   
  }, [caseRequest.state]);

  const totalBytes = useMemo(() => {
    return attachmentsBytes + (includeReport && pdfSizeBytes ? pdfSizeBytes : 0);
  }, [attachmentsBytes, includeReport, pdfSizeBytes]);

  useEffect(() => {
    setPdfSizeError('');
  }, [attachmentsBytes, includeReport]);

  const limitBytes = DIRECT_MESSAGE_SIZE_LIMIT_MB * 1_000_000;
  
  const totalWithinLimit = isUnderMBLimit({
    totalBytes,
    limitSize: DIRECT_MESSAGE_SIZE_LIMIT_MB,
    conservative: true,
    marginBytes: 0
  });
  const showSizeSummary = totalBytes > limitBytes;
  const totalSizeLabel = convertBytesAutoUnit(totalBytes);
  return (
    <Dialog
      title={"Compose direct message"}
      preValidation={preValidation}
      closeDialog={closeDialog}
    ><WithForm
      alwaysSave={true}
      action={(values) => {
        const valuesToSubmit = {...values, medicalRecordsIds: selectedDirectMessageRecords.map(v => Number(v))}
        return jwtPostFetcher(getJwt())("/api/admin/directMessage?requestId=" + (caseRequest.requestId || caseRequest.id), valuesToSubmit)
          .then((_response) => {
            setNotification({ type: "success", msg: "Direct message sent successfully." })
            closeDialog()
          })
          .catch(error => {
            console.error(error)

            const defaultAlert = "It was not possible to send the direct message.";
            let handled = false;

            if (Array.isArray(error)) {
              const [sizeInBytes, response] = error;
              const errorCode = response?.status || response?.statusCode || 0;
              const size = sizeInBytes || 0;

              if (errorCode === 468) {
                setPdfSizeError(`The total size of selected files is ${convertBytesAutoUnit(size)} which exceeds the ${DIRECT_MESSAGE_SIZE_LIMIT_MB} MB limit for Direct Messaging. Please remove some files.`);
                handled = true;
              } else if (errorCode === 467) {
                const combinedSize = size + attachmentsBytes;
                setPdfSizeBytes(size);
                setPdfSizeError(`The total size of selected files plus the case report is ${convertBytesAutoUnit(combinedSize)} which exceeds the ${DIRECT_MESSAGE_SIZE_LIMIT_MB} MB limit for Direct Messaging. Please remove some files or exclude the case report.`);
                handled = true;
              }
            }

            // if (!handled) {
            //   setPdfSizeError(defaultAlert);
            // }

            setNotification({ type: "alert", msg: defaultAlert })
            return Promise.reject(error);
          })
      }}
      preValidation={preValidation}
      onSuccess={onSuccess}
      onChange={handleFormValuesChange}
      values={{
        "subject": subject,
        "medicalRecordNumber": caseRequest?.caseInfo?.ehrInfo?.mrn || "",
        "orderNumber": caseRequest?.caseInfo?.ehrInfo?.orderNumber || "",
        "message": message,
        "includeReport": false,
      }}
    >{form => {
      formApiRef.current = form;
      return <>
      <p>Patient: <FormattedMessage
        defaultMessage="{firstName} {middleName} {lastName}"
        description="Case request card patient name"
        values={{
          firstName,
          middleName,
          lastName,
        }}
      /> - Case: {caseRequest.requestCode}</p>

      <MailAdressesCard>
        <p data-testid="direct-message-from"><b>From:</b> {providerInfoSec?.directMessageInfo?.from}</p>
        <p data-testid="direct-message-to"><b>To:</b> {providerInfoSec?.directMessageInfo?.to}</p>
      </MailAdressesCard>
      <SplitColumnsContainer columns={2} gap="1rem">
        <TextInput
          tabindex="1"
          tabIndex="1"
          path="medicalRecordNumber"
          label={"Medical Record Number"}
        />
        <TextInput
          tabindex="1"
          tabIndex="1"
          path="orderNumber"
          label={"Order Number"}
        />
      </SplitColumnsContainer>

      <Divider />

      <TextInput
        tabindex="1"
        tabIndex="1"
        path="subject"
        label={"Subject"}
        mandatory
      />

      <TextInput
        data-testid="direct-message"
        tabindex="1"
        tabIndex="1"
        textarea
        autoResize
        path="message"
        label={"Message"}
        mandatory
      />

      <SectionLabel>Attachments</SectionLabel>

      <SplitColumnsContainer>
        <AttachamentsBox onClick={() => {
            // Open file selector
            fileDialog.toggle();
          }}
        >
          <span>{selectedDirectMessageRecords.length} medical files</span>
          <TextButton>Select</TextButton>
        </AttachamentsBox>

        <Tooltip content={caseRequest.state !== CASE_CLOSED ? "Include case only available for completed cases" : "Include case"}>
          <ToggleCheckbox
            disabled={caseRequest.state !== CASE_CLOSED}
            css={(caseRequest.state !== CASE_CLOSED ) && "opacity: 0.5;"}
            label={<ToggleLabel>Include case</ToggleLabel>}
            path="includeReport"
          />
        </Tooltip>
      </SplitColumnsContainer>
      {showSizeSummary && (
        <SizeSummary>
          Total size: {totalSizeLabel}
          {includeReport && pdfSizeBytes ? ' (includes case report PDF)' : ''}
        </SizeSummary>
      )}
      {pdfSizeError && <SizeError>{pdfSizeError}</SizeError>}
      {!pdfSizeError && !totalWithinLimit && showSizeSummary && (
        <SizeError>
          Total size exceeds {DIRECT_MESSAGE_SIZE_LIMIT_MB} MB limit for Direct Messaging. Remove some files or exclude the case report.
        </SizeError>
      )}
      {fileDialog.isOpen && (
        <DirectMessageRecordsSelectorDialog
          fileDialog={fileDialog}
          caseRequest={caseRequest}
          setCaseRequest={setCaseRequest}
          selectedDirectMessageRecords={selectedDirectMessageRecords}
          setSelectedDirectMessageRecords={setSelectedDirectMessageRecords}
          extraAttachmentBytes={includeReport && pdfSizeBytes ? pdfSizeBytes : 0}
          sizeLimitMB={DIRECT_MESSAGE_SIZE_LIMIT_MB}
          
        />
      )}
      <Errors />
      <div css="position: relative; text-align: right; margin-top:2rem;">
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          icon="mail"
          highlight
          onClick={() => {
            form.submitForm()
          }}
          disabled={form.isLoading || !!pdfSizeError || !totalWithinLimit}
          isLoading={form.isLoading}
        >Send</Button>
      </div>
    </>
    }}</WithForm>
    </Dialog>
  );
};
